import { useState, startTransition } from 'react';
import { isGlobalProgressStatus } from '../../../constants/status';

/**
 * Hook para gerenciar o estado "busy" do GlobalProgress
 *
 * Previne desmontagem prematura do componente enquanto a fila de textos está sendo processada.
 *
 * Problema resolvido:
 * Quando múltiplos status são criados rapidamente (ex: "Verificando..." → "Analisando..."),
 * o GlobalProgress implementa uma fila FIFO. Quando o status externo vira null, o componente
 * ainda pode ter textos na fila para exibir. Este hook mantém o estado "busy" ativo até que
 * a fila esteja completamente processada.
 *
 * Fluxo:
 * 1. Status inicial define globalProgressBusy (true se tem status)
 * 2. GlobalProgress chama handleBusyChange(true) quando inicia processamento da fila
 * 3. GlobalProgress chama handleBusyChange(false) quando termina de processar tudo
 * 4. DynamicWrapper usa globalProgressBusy para decidir se mantém GlobalProgress montado
 *
 * Usa startTransition para evitar que mudanças de estado busy bloqueiem transições urgentes.
 *
 * @param {Object|null} initialStatus - Status inicial com { type: string, text: string }
 * @returns {Object} Estado e callbacks
 * @returns {boolean} returns.globalProgressBusy - Se o GlobalProgress está ocupado processando a fila
 * @returns {Function} returns.handleBusyChange - Callback para atualizar estado busy: (isBusy: boolean) => void
 *
 * @example
 * const { globalProgressBusy, handleBusyChange } = useProgressBusyState(status);
 *
 * // Passa handleBusyChange para GlobalProgress
 * <GlobalProgress status={status} onBusyChange={handleBusyChange} />
 *
 * // Usa globalProgressBusy para decidir se mostra GlobalProgress
 * const shouldShow = isGlobalProgressStatus(displayStatus) || globalProgressBusy;
 */
export const useProgressBusyState = (initialStatus) => {
  const [globalProgressBusy, setGlobalProgressBusy] = useState(
    isGlobalProgressStatus(initialStatus),
  );

  const handleBusyChange = (isBusy) => {
    startTransition(() => {
      setGlobalProgressBusy(isBusy);
    });
  };

  return { globalProgressBusy, handleBusyChange };
};
